import { computed, ref } from 'vue';

// Light / dark theme. The viewer picks System, Light or Dark; System follows
// the operating system and keeps following it if it changes. The choice is a
// per-browser convenience in localStorage, and the resolved theme is a `dark`
// class on <html>, which both tokens.css and the MIT PrimeVue theme read.
//
// index.html applies the same logic in an inline script before the app
// loads, so a dark-mode viewer never sees a white flash. Keep the two in step:
// same storage key, same values.

export type ThemeChoice = 'system' | 'light' | 'dark';

export const THEME_STORAGE_KEY = 'parley.theme';

function readChoice(): ThemeChoice {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    return 'system';
  }
}

function writeChoice(choice: ThemeChoice) {
  try {
    if (choice === 'system') localStorage.removeItem(THEME_STORAGE_KEY);
    else localStorage.setItem(THEME_STORAGE_KEY, choice);
  } catch {
    // Keep the choice for this visit only.
  }
}

const media =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;

const choice = ref<ThemeChoice>(readChoice());
const systemDark = ref(media?.matches ?? false);
media?.addEventListener?.('change', (e) => {
  systemDark.value = e.matches;
  apply();
});

const isDark = computed(() =>
  choice.value === 'system' ? systemDark.value : choice.value === 'dark'
);

// Readable from plain modules (chart palettes), so a computed that calls
// them re-runs when the theme changes.
export const themeIsDark = isDark;

function apply() {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', isDark.value);
}

apply();

const ORDER: ThemeChoice[] = ['system', 'light', 'dark'];

export function useTheme() {
  function setChoice(next: ThemeChoice) {
    choice.value = next;
    writeChoice(next);
    apply();
  }

  function cycle() {
    setChoice(ORDER[(ORDER.indexOf(choice.value) + 1) % ORDER.length]);
  }

  return { choice, isDark, setChoice, cycle };
}
